import { Component, Input, OnInit } from '@angular/core';
import { MaterialModule } from '../../../material/material.module';
import { CommonModule } from '@angular/common';
import * as Highcharts from 'highcharts';
import HighchartsMore from 'highcharts/highcharts-more';
import HighchartsNoData from 'highcharts/modules/no-data-to-display';
import HighchartsBoost from 'highcharts/modules/boost';
import { ChartSharedService } from '../../services/chart-shared.service';
import AccessibilityModule from 'highcharts/modules/accessibility';
import { combineLatest } from 'rxjs';

AccessibilityModule(Highcharts);
HighchartsMore(Highcharts);
HighchartsNoData(Highcharts);
HighchartsBoost(Highcharts);

@Component({
  selector: 'app-chart-inversion',
  standalone: true,
  imports: [MaterialModule, CommonModule],
  templateUrl: './chart-inversion.component.html',
  styleUrl: './chart-inversion.component.less',
})
export class ChartInversionComponent implements OnInit {
  @Input() title!: string;

  Highcharts: typeof Highcharts = Highcharts;
  chartOptions: Highcharts.Options = {};
  chartData: {
    capital: number;
    ganancia_actual: number;
    ganancia_al_vencimiento: number;
  }[] = [];
  totalGananciaActual!: number;
  totalGananciaAlVencimiento!: number;
  totalCapital!: number;

  currentDate: string = new Date().toISOString().split('T')[0];

  fechaVencimiento: string = '';
  fechaEmision: string = '';

  constructor(private graphService: ChartSharedService) {}

  ngOnInit() {
    combineLatest([
      this.graphService.detalleInversionesData$,
      this.graphService.fechaEmision$,
      this.graphService.fechaVencimiento$
    ]).subscribe({
      next: ([data, fechaEmision, fechaVencimiento]) => {
        try {
          if (data && fechaEmision && fechaVencimiento) {
            this.processSimulacionData(data.info.data.investments_dict);
            this.fechaEmision = fechaEmision;
            this.fechaVencimiento = fechaVencimiento;
            this.loadChart();
          } else {
            console.log('Datos no válidos o vacíos');
          }
        } catch (error) {
          console.log('Error en el procesamiento de datos:', error);
        }
      },
      error: (err) => {
        console.log('Error en el observable:', err);
      }
    });
  }

  processSimulacionData(inversion: any) {
    const typedData = inversion as {
      capital: number;
      ganancia_actual: number;
      ganancia_al_vencimiento: number;
    }[];
    if (typedData && typedData.length > 0) {
      this.chartData = typedData.map((item) => ({
        capital: item['capital'],
        ganancia_actual: item['ganancia_actual'],
        ganancia_al_vencimiento: item['ganancia_al_vencimiento'],
      }));

      this.totalCapital = this.chartData.reduce((acc, item) => acc + item.capital, 0);
      this.totalGananciaActual = this.totalCapital + this.chartData.reduce((acc, item) => acc + item.ganancia_actual, 0);
      this.totalGananciaAlVencimiento = this.totalCapital + this.chartData.reduce( (acc, item) => acc + item.ganancia_al_vencimiento, 0);


    } else {
      console.error('Datos no válidos o vacíos');
    }
  }

  loadChart() {
    const totalCapitalData = [
      {
        name: 'capital',
        y: this.totalCapital,
        date: this.formatDate(this.fechaEmision),
      },
    ];
    
    const totalGananciaActualData = [
      {
        name: 'hoy',
        y: this.totalGananciaActual,
        date: this.formatDate(this.currentDate),
      },
    ];
    
    const totalGananciaAlVencimientoData = [
      {
        name: 'vencimiento',
        y: this.totalGananciaAlVencimiento,
        date: this.formatDate(this.fechaVencimiento),
      },
    ];
    

    this.chartOptions = {
      colors: ['#000467', '#5258a5', '#a5abd6'],
      chart: {
        type: 'column',
        renderTo: 'container-inversion',
        backgroundColor: 'white',
        style: {
          fontFamily: 'DM Sans',
        },
      },
      credits: {
        enabled: false,
      },
      title: {
        text: '',
        align: 'left',
      },
      xAxis: {
        visible: false,
      },
      yAxis: {
        visible: false,
      },
      legend: {
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'top',
        itemMarginBottom: 5,
        itemDistance: 100,
        itemStyle: {
          lineHeight: '20px',
        },
      },
      tooltip: {
        headerFormat: '',
        pointFormatter: function (this: any) {
          const formattedDate = this.date;
          return `
          <div style="display: flex; flex-direction: column; align-items: center;">
            <span style="color:${this.series.color};"><b>Inversión ${this.name}:</b></span>
            <span><b>$${Highcharts.numberFormat(this.y, 0, ',', '.')}</b></span>
            <span style="margin-top: 10px;"><b>${formattedDate}</b></span>
          </div>`;
        },
        useHTML: true,
      },
      
      plotOptions: {
        column: {
          dataLabels: {
            enabled: true,
            verticalAlign: 'top',
            y: -30,
            formatter: function (this: any) {
              return '$' + Highcharts.numberFormat(this.y, 0, ',', '.');
            },
          },
          pointPadding: 0.2
        },
      },
      series: [
        {
          type: 'column',
          name: `Capital<br> (${this.formatDate(this.fechaEmision)})`,
          data: totalCapitalData,
        } as unknown as Highcharts.SeriesBarOptions,
        {
          type: 'column',
          name: `Hoy<br> (${this.formatDate(this.currentDate)})`,
          data: totalGananciaActualData,
        } as unknown as Highcharts.SeriesBarOptions,
        {
          type: 'column',
          name: `Vencimiento<br> (${this.formatDate(this.fechaVencimiento)})`,
          data: totalGananciaAlVencimientoData,
        } as unknown as Highcharts.SeriesBarOptions,
      ],
    };

    if (document.getElementById('container-inversion')) {
      Highcharts.chart('container-inversion', this.chartOptions);
    } else {
      console.log('Chart container not found');
    }
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    
    const day = date.getUTCDate();
    const month = date.toLocaleString('es-ES', { month: 'long' });
    const year = date.getUTCFullYear();
    
    return `${day} ${month} ${year}`;
  }
  
}
